/* ===========================================================================
 * THE ICON SCALE.
 *
 * The audit counted TWELVE icon sizes in use across the app — 11, 12, 13, 14,
 * 15, 16, 18, 19, 20, 22, 24, 26 — and one file sizing icons by CSS class
 * instead of the prop. That is not a style; it is twelve people each choosing
 * once, in a codebase whose type, spacing, radius and elevation are all closed
 * scales.
 *
 * It happened for a mechanical reason worth naming: every `size={n}` is a bare
 * NUMBER in TSX, so the design-token gate — which reads class strings — cannot
 * see any of them. Nothing was enforcing anything, and nothing could.
 *
 * THE RAIL ALREADY PROVED THE FIX. `Sidebar.tsx` declares exactly two tiers and
 * pins them with a test that checks every glyph's path data, and the rail is
 * the one surface in the app with no icon drift at all. This is that discipline,
 * hoisted somewhere the other sixty screens can reach it.
 *
 * FOUR STEPS, each named for the JOB rather than the pixel — a caller asking
 * for `ICON.control` cannot pick 15 by accident, and a reviewer reading
 * `ICON.inline` knows what it is FOR:
 *
 *   inline   13  beside a word, inside a line of text: a chip's glyph, the
 *                security line under the sign-in card, a flag marker in a row.
 *                Below `--text-copy`'s cap height, so it does not out-weigh the
 *                word it sits next to.
 *   control  16  inside a button, a menu item, a toolbar tool, a table cell
 *                action. The overwhelming majority — 30+ existing sites are
 *                already 16, so this step is the app's de-facto standard made
 *                de-jure.
 *   feature  20  a section heading's glyph, an empty state's mark, a stat
 *                tile's icon — an icon that is an OBJECT rather than a
 *                decoration on one.
 *   hero     28  the one glyph on an empty page or a dialog's opening mark.
 *                Rare by design: if a screen needs two, one of them is `feature`.
 *
 * WHAT IS DELIBERATELY NOT HERE: 11 and 12. The audit found `size={11}` drawing
 * a 0.92px stroke — below the type scale's own floor, and invisible on the camp
 * tablet this product actually runs on. There is no step for it because there
 * is no legitimate use for it.
 *
 * The map is `as const` so a typo is a compile error rather than a silent
 * fallback, and the values are plain numbers because that is what lucide's
 * `size` prop takes — wrapping them in a component would put a second control
 * language in front of every icon in the app to save nothing.
 * ======================================================================== */

export const ICON = {
  /** 13 — beside a word, inside a line of text. */
  inline: 13,
  /** 16 — inside a button, menu item, toolbar tool or row action. The default. */
  control: 16,
  /** 20 — a section mark, an empty state, a tile's own glyph. */
  feature: 20,
  /** 28 — the single mark on an empty page or a dialog. */
  hero: 28,
} as const;

export type IconStep = keyof typeof ICON;
